import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import { visuallyHidden } from '@mui/utils';
import CustomTablePagination from 'components/CustomTablePagination';
import { useLayoutEffect, useRef, useState } from 'react';
import { EmptyMessage, Load, LoadCenter } from 'ui/styled';
import { TabledRowText } from './styled';

const BORDER_GAP = 4;
// --- Estilos ---
// Maiko Rodrigo Defreyn
// ---------------------------------------------------------
// Novo estilo com colunas fixas e background branco entre colunas mas sem espaçamento entre linhas
const StyledTable = styled(Table)({
  tableLayout: 'auto',
  width: '100%',
  borderCollapse: 'separate',
  borderSpacing: '0',
});

const StyledTableCell = styled(TableCell, {
  shouldForwardProp: (prop) => prop !== 'fixed' && prop !== 'left',
})(({ fixed, left }) => ({
  position: 'relative',
  borderRight: `${BORDER_GAP}px solid #ffffff !important`,
  backgroundClip: 'padding-box !important',

  [`&.${tableCellClasses.head}`]: {
    whiteSpace: 'nowrap',
    fontSize: 12,
    fontWeight: 600,
    textTransform: 'uppercase',
    fontFamily: 'Kumbh Sans',
    position: 'sticky',
    top: 0,
    zIndex: fixed ? 20 : 10,
    backgroundColor: '#d5d5d5ff',

    ...(fixed && { left }),
  },

  [`&.${tableCellClasses.body}`]: {
    BorderColor: '#ffffff',
    Border: '4px',
    whiteSpace: 'nowrap',
    fontSize: 14,
    fontWeight: 400,
    fontFamily: 'Kumbh Sans',
    padding: '8px 12px',
    marginBottom: `${BORDER_GAP}px`,
    backgroundColor: 'inherit',

    ...(fixed && {
      position: 'sticky',
      left,
      zIndex: 11,
      backgroundColor: '#d5d5d5ff',
    }),
  },
}));
// ---------------------------------------------------------

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.colors.lightshadow,
  },
  '&:nth-of-type(even)': {
    backgroundColor: theme.palette.colors.light,
  },
}));

// --- Funções Auxiliares ---
function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// --- Componente Principal ---
export default function BasicTable({
  columns,
  rows,
  loading,
  pagination,
  fixedColumns = 0,
}) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('');

  // Maiko Defreyn
  // ---------------------------------------------------------
  const [fixedOffsets, setFixedOffsets] = useState([]);
  const headerRefs = useRef([]);

  // Cálculo dinâmico do tamanho das colunas no render inicial
  useLayoutEffect(() => {
    if (!fixedColumns) {
      setFixedOffsets([]);
      return;
    }
    const offsets = [];
    let left = 0;
    for (let i = 0; i < fixedColumns; i++) {
      const cell = headerRefs.current[i];
      if (!cell) {
        continue;
      }
      offsets[i] = left;
      left += cell.offsetWidth;
    }
    setFixedOffsets(offsets);
  }, [columns, fixedColumns, rowsPerPage]);
  // ---------------------------------------------------------

  // Manipula mudança de página
  const handleChangePage = (event, newPage) => setPage(newPage);

  // Manipula mudança de linhas por página
  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0); // Reseta para a primeira página
  };

  // Manipula ordenação
  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Ordenação estável
  const stableSort = (array, comparator) => {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) {
        return order;
      }
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  };

  // Aplica ordenação
  const sortedRows = stableSort(rows, getComparator(order, orderBy));

  // Aplica paginação
  const paginatedRows =
    rowsPerPage > 0
      ? sortedRows.slice(page * rowsPerPage, (page + 1) * rowsPerPage)
      : sortedRows;

  // Renderiza o corpo da tabela
  const TableContent = () => {
    if (loading) {
      return (
        <LoadCenter>
          <Load />
        </LoadCenter>
      );
    }

    if (!rows?.length) {
      return (
        <TableBody>
          <TableRow>
            <StyledTableCell
              colSpan={columns?.length || 1}
              style={{ textAlign: 'center' }}
            >
              <EmptyMessage style={{ margin: '0px', fontSize: '15px' }}>
                Nenhum registro encontrado.
              </EmptyMessage>
            </StyledTableCell>
          </TableRow>
        </TableBody>
      );
    }
    return (
      <TableBody>
        {paginatedRows.map((row, rowIndex) => (
          <StyledTableRow key={rowIndex}>
            {/* <TableCell>
              <IconButton
                aria-label="expand row"
                size="small"
                onClick={() => setOpen(!open)}
              >
                {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
            </TableCell> */}
            {columns?.map((item, colIndex) => (
              <StyledTableCell
                key={colIndex}
                align={item.align || 'left'}
                fixed={colIndex < fixedColumns}
                left={fixedOffsets[colIndex]}
              >
                {item.renderCell ? item.renderCell({ row }) : row[item.ref]}
              </StyledTableCell>
            ))}
          </StyledTableRow>
        ))}
      </TableBody>
    );
  };

  return (
    <>
      <TableContainer
        component={Paper}
        style={{
          position: 'relative',
          boxShadow: 'none',
          maxHeight: rowsPerPage > 10 ? 600 : 'none',
          overflowY: rowsPerPage > 10 ? 'auto' : 'visible',
          overflowX: 'auto',
        }}
      >
        <StyledTable aria-label="custom table" sx={{ minWidth: 80 }}>
          <TableHead>
            <TableRow>
              {columns?.map((item, key) => (
                <StyledTableCell
                  key={key}
                  ref={(el) => (headerRefs.current[key] = el)}
                  align={item.align || 'left'}
                  sortDirection={orderBy === item.ref ? order : false}
                  fixed={key < fixedColumns}
                  left={fixedOffsets[key]}
                >
                  {item.sortable ? (
                    <TableSortLabel
                      active={orderBy === item.ref}
                      direction={orderBy === item.ref ? order : 'asc'}
                      onClick={(event) => handleRequestSort(event, item.ref)}
                    >
                      <TabledRowText>{item.title}</TabledRowText>
                      {orderBy === item.ref && (
                        <span style={visuallyHidden}>
                          {order === 'desc'
                            ? 'sorted descending'
                            : 'sorted ascending'}
                        </span>
                      )}
                    </TableSortLabel>
                  ) : (
                    <TabledRowText>{item.title}</TabledRowText>
                  )}
                </StyledTableCell>
              ))}
            </TableRow>
          </TableHead>
          {TableContent()}
        </StyledTable>
      </TableContainer>

      {pagination && (
        <CustomTablePagination
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      )}
    </>
  );
}
